/**
 * Share menu / shopping list helpers (Phase 14).
 *
 * Calls POST /api/v1/menus/{menuId}/share to create a share link, upgrades
 * it to a referral link (`?ref=<code>`, POST /api/v1/referrals/from-share)
 * so the ambassador programme can credit the sharer, then uses the Capacitor
 * Share API on native or clipboard copy on web.
 */
import { Share } from "@capacitor/share";
import { api } from "@/lib/api";
import { isNative } from "@/lib/capacitor";
import { createReferralLinkForToken } from "@/lib/referral";

export interface ShareCreateRequest {
  scope: "menu" | "shopping_list" | "both";
  expires_in_days?: number | null;
}

export interface ShareCreateResponse {
  share_url: string;
  token: string;
  expires_at: string;
}

/**
 * Best-effort: attach the sharer's referral code to the link. A referral
 * failure must never block sharing, so fall back to the plain share URL.
 */
async function withReferral(res: ShareCreateResponse): Promise<string> {
  try {
    const referral = await createReferralLinkForToken(res.token);
    return referral.share_url || res.share_url;
  } catch {
    return res.share_url;
  }
}

/**
 * Create a share link and invoke the platform share sheet (native) or
 * copy to clipboard (web). Returns the shared URL so callers can handle
 * it differently if needed.
 */
export async function shareMenu(
  menuId: string,
  scope: ShareCreateRequest["scope"] = "menu",
  expiresInDays: number = 30,
): Promise<string> {
  const res = await api<ShareCreateResponse>(`/api/v1/menus/${menuId}/share`, {
    method: "POST",
    body: { scope, expires_in_days: expiresInDays } as ShareCreateRequest,
  });

  const url = await withReferral(res);

  if (isNative()) {
    await Share.share({
      url,
      title: "Mon menu Batch Cooking",
      dialogTitle: "Partager mon menu",
    });
  } else {
    await navigator.clipboard.writeText(url);
  }

  return url;
}
